import React, {useEffect, useRef, useState} from 'react';
import './App.css';
import MessageComponent from "./components/MessageComponent";
import {useMessageHandler} from "./components/useMessageHandler";
import {useAppSelector} from "./hooks";
import {selectSystemName} from "./redux/system";
import {ClipLoader} from "react-spinners";
import {selectPlayingChess} from "./redux/chess";
import SimpleChess from "./components/SimpleChess";
import {selectLogEditor} from "./redux/ui";
import LogEditor from "./components/LogEditor";
import {selectFrontingName} from "./redux/members";
import {selectMessages} from "./redux/messages";

function App() {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<string[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const frontName = useAppSelector(selectFrontingName)
  const logEditorEnabled = useAppSelector(selectLogEditor)
  const messages = useAppSelector(selectMessages)

  const [value, setValue] = useState('');

  const inputAreaRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const windowBottomRef = useRef<HTMLDivElement>(null);

  const { handleMessage } = useMessageHandler();
  const systemName = useAppSelector(selectSystemName);
  const playingChess = useAppSelector(selectPlayingChess)

  const onChange = (value: string) => {
    setValue(value);

    if (!inputRef.current) {
      return
    }

    let height = inputRef.current.scrollHeight;
    if (height > 50) {
      if (value === '') {
        height = 50;
      }

      setTerminalHeight(height);
    }
  }

  const setTerminalHeight = (height: number) => {
    if (!inputAreaRef.current || !inputRef.current) {
      return;
    }

    const newHeight = Math.min(height, 100) + 'px'
    inputAreaRef.current.style.height = newHeight;
    inputAreaRef.current.style.lineHeight = newHeight;
    inputRef.current.style.height = newHeight;
  }

  const handleKeyPress = async (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      const indexChange = e.key === 'ArrowUp' ? 1 : -1;
      let index = historyIndex + indexChange;
      if (history[index] === undefined) {
        index = 0;
      }
      setHistoryIndex(index);

      const command = history[index]
      if (command) {
        onChange(command)
        setTimeout(() => {
          if (inputRef.current) {
            inputRef.current.setSelectionRange(command.length, command.length)
          }
        }, 100)
      }
    }

    if (e.key === 'Enter' && !e.shiftKey) {
      setLoading(true)

      setHistory([
        value,
        ...history
      ]);
      setHistoryIndex(-1);

      await handleMessage(value);

      setValue('');
      setTerminalHeight(50);
      setLoading(false)
      e.preventDefault();
      e.stopPropagation();
    }
  }

  useEffect(() => {
    if (windowBottomRef.current) {
      windowBottomRef.current.scrollIntoView({ behavior: 'auto' });
    }
  }, [messages.length])

  useEffect(() => {
    setTerminalHeight(50)
  }, [])

  if (logEditorEnabled) {
    return (
      <LogEditor />
    )
  }

  return (
    <div className={'terminal'}>
      <div className={'window'}>
        <div className={'system-name'}>
          <span>{systemName || 'Systemic'}</span>
        </div>

        {[...messages].sort((m1, m2) => new Date(m1.createdAt).getTime() - new Date(m2.createdAt).getTime()).map((m) => {
          return <MessageComponent key={m.id} message={m} />
        })}

        {loading && (
          <ClipLoader color={'#eee'} size={20} cssOverride={{
            marginLeft: 10
          }} />
        )}

        {playingChess && (
          <div className={'chess'}>
            <SimpleChess />
          </div>
        )}

        <div style={{ float:"left", clear: "both" }}
             ref={windowBottomRef}>
        </div>
      </div>
      <div className={'input-area'} ref={inputAreaRef}>
        {frontName !== '' && (
          <div className={'input-username'}>
            <span>{frontName}</span>
          </div>
        )}
        <div className={'input-caret'}>
          <span>&gt;</span>
        </div>
        <div className={'input'}>
          <textarea
            ref={inputRef}
            onChange={(e: any) => {
              onChange(e.target.value)
            }}
            onKeyDown={(e) => handleKeyPress(e)}
            value={value}
            autoFocus={true}
            placeholder={"Say hi"}
          />
        </div>
      </div>
    </div>
  );
}

export default App;
